import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AdminAuthProvider, useAdminAuth } from '../context/AdminAuthContext';
import AdminLogin from './AdminLogin';
import MarketingAnalytics from './MarketingAnalytics';
import LeadsCRM from './LeadsCRM';
import EnquiryDetail from './EnquiryDetail';
import PartnersCRM from './PartnersCRM';
import PartnerDetail from './PartnerDetail';
import UserManagement from './UserManagement';
import ContentList from './ContentList';
import ContentEditor from './ContentEditor';
import NewsletterList from './NewsletterList';
import NewsletterEditor from './NewsletterEditor';
import SubscribersList from './SubscribersList';
import ContactsCRM from './ContactsCRM';
import ContactDetail from './ContactDetail';
import CompaniesCRM from './CompaniesCRM';
import CompanyDetail from './CompanyDetail';
import Retargeting from './Retargeting';
import SubscriptionsCRM from './SubscriptionsCRM';
import SubscriptionDetail from './SubscriptionDetail';
import Settings from './Settings';
import Inbox from './Inbox';
import CalendarConnect from './CalendarConnect';
import TechConfig from './TechConfig';

function RequireAuth({ adminOnly = false, children }) {
  const { user, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-6 w-6 animate-spin text-[#0035A8]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (adminOnly && user.role !== 'admin') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="admin-forbidden">
          <h1 className="text-xl font-bold text-slate-900">Admin access required</h1>
          <p className="mt-2 text-sm text-slate-600">This section is restricted to Admin users.</p>
        </div>
      </div>
    );
  }
  return children;
}

export default function AdminApp() {
  return (
    <AdminAuthProvider>
      <div className="bp-admin">
        <Routes>
          <Route path="login" element={<AdminLogin />} />
          <Route path="dashboard" element={<RequireAuth><MarketingAnalytics /></RequireAuth>} />
          <Route path="analytics" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="companies" element={<RequireAuth><CompaniesCRM /></RequireAuth>} />
          <Route path="companies/:id" element={<RequireAuth><CompanyDetail /></RequireAuth>} />
          <Route path="contacts" element={<RequireAuth><ContactsCRM /></RequireAuth>} />
          <Route path="contacts/:id" element={<RequireAuth><ContactDetail /></RequireAuth>} />
          <Route path="retargeting" element={<RequireAuth><Retargeting /></RequireAuth>} />
          <Route path="subscriptions" element={<RequireAuth><SubscriptionsCRM /></RequireAuth>} />
          <Route path="subscriptions/:id" element={<RequireAuth><SubscriptionDetail /></RequireAuth>} />
          <Route path="enquiries" element={<RequireAuth><LeadsCRM /></RequireAuth>} />
          <Route path="inbox" element={<RequireAuth><Inbox /></RequireAuth>} />
          <Route path="enquiries/:id" element={<RequireAuth><EnquiryDetail /></RequireAuth>} />
          <Route path="partners" element={<RequireAuth><PartnersCRM /></RequireAuth>} />
          <Route path="partners/:id" element={<RequireAuth><PartnerDetail /></RequireAuth>} />
          <Route path="leads" element={<Navigate to="/admin/enquiries" replace />} />
          <Route path="content" element={<Navigate to="/admin/content/blogs" replace />} />
          <Route path="content/:type" element={<RequireAuth><ContentList /></RequireAuth>} />
          <Route path="content/:type/new" element={<RequireAuth><ContentEditor /></RequireAuth>} />
          <Route path="content/:type/:id" element={<RequireAuth><ContentEditor /></RequireAuth>} />
          <Route path="newsletters" element={<RequireAuth><NewsletterList /></RequireAuth>} />
          <Route path="newsletters/new" element={<RequireAuth><NewsletterEditor /></RequireAuth>} />
          <Route path="newsletters/:id" element={<RequireAuth><NewsletterEditor /></RequireAuth>} />
          <Route path="subscribers" element={<RequireAuth><SubscribersList /></RequireAuth>} />
          <Route path="users" element={<RequireAuth adminOnly><UserManagement /></RequireAuth>} />
          <Route path="settings" element={<RequireAuth adminOnly><Settings /></RequireAuth>} />
          <Route path="calendar" element={<RequireAuth><CalendarConnect /></RequireAuth>} />
          <Route path="tech-config" element={<RequireAuth adminOnly><TechConfig /></RequireAuth>} />
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </div>
    </AdminAuthProvider>
  );
}
